import { Command } from 'commander';
import inquirer from 'inquirer';
import chalk from 'chalk';
import path from 'path';
import fs from 'fs-extra';
import { configManager } from '../core/config-manager.js';
import { gitDownloader } from '../core/git-downloader.js';
import { templateRenderer } from '../core/template-renderer.js';
import { logger } from '../utils/logger.js';
import { validateProjectName } from '../utils/validator.js';
import { checkDirectoryStatus } from '../utils/file.js';
import { startSpinner, succeedSpinner, failSpinner } from '../utils/spinner.js';
import { DirectoryStatus, ConflictAction, CreateOptions } from '../types/index.js';
import { processTemplateFiles } from '../utils/file.js';

/**
 * 创建项目命令
 */
export function registerCreateCommand(program: Command) {
  program
    .command('create <project-name>')
    .description('创建新项目')
    .option('-t, --template <name>', '指定模板名称')
    .action(async (projectName: string, options: CreateOptions) => {
      try {
        // 验证项目名称
        const validation = validateProjectName(projectName);
        if (validation !== true) {
          logger.error(validation as string);
          process.exit(1);
        }

        // 获取所有模板
        const templates = await configManager.getTemplates();
        const templateList = Object.values(templates);

        if (templateList.length === 0) {
          logger.error('暂无可用模板');
          logger.info('请先使用 "zh template add" 添加模板');
          process.exit(1);
        }

        // 选择模板
        let selectedTemplate = options.template;
        if (!selectedTemplate) {
          const { template } = await inquirer.prompt([
            {
              type: 'list',
              name: 'template',
              message: '选择项目模板:',
              choices: templateList.map((tpl) => ({
                name: `${tpl.name}`,
                value: tpl.name,
              })),
            },
          ]);
          selectedTemplate = template;
        }

        const template = await configManager.getTemplate(selectedTemplate as string);
        if (!template) {
          logger.error(`模板 ${chalk.cyan(selectedTemplate)} 不存在`);
          process.exit(1);
        }

        // 检查目标目录
        const targetDir = path.resolve(process.cwd(), projectName);
        const dirStatus = await checkDirectoryStatus(targetDir);

        // 处理目录冲突
        if (dirStatus === DirectoryStatus.HAS_CONTENT) {
          logger.warning(`目录 "${projectName}" 已存在且不为空`);
          logger.newLine();

          const { action } = await inquirer.prompt([
            {
              type: 'list',
              name: 'action',
              message: '如何处理？',
              choices: [
                { name: '取消创建', value: ConflictAction.CANCEL },
                { name: '清空目录并继续', value: ConflictAction.OVERWRITE },
              ],
            },
          ]);

          if (action === ConflictAction.CANCEL) {
            logger.info('已取消创建');
            return;
          }

          if (action === ConflictAction.OVERWRITE) {
            await fs.emptyDir(targetDir);
          }
        }

        // 确保目录存在
        await fs.ensureDir(targetDir);

        logger.newLine();
        logger.info(`正在创建项目 ${chalk.cyan(projectName)}...`);
        logger.newLine();

        // 下载模板
        const spinner = startSpinner('正在下载模板...');
        try {
          await gitDownloader.download(template.source, targetDir);
          succeedSpinner('模板下载完成');
        } catch (error: any) {
          failSpinner('模板下载失败');
          logger.error(error.message);
          // 清理失败的下载
          await fs.remove(targetDir);
          process.exit(1);
        }

        // 读取模板配置
        const templateConfig = await templateRenderer.readTemplateConfig(targetDir);

        let answers: Record<string, any> = { projectName };

        if (templateConfig && templateConfig.prompts) {
          logger.newLine();
          logger.info('请配置项目信息：');
          logger.newLine();
          answers = await templateRenderer.collectAnswers(templateConfig, projectName);
        }

        // 处理模板文件（变量替换）
        startSpinner('正在生成项目文件...');
        try {
          await processTemplateFiles(
            targetDir,
            answers,
            templateConfig?.filters
          );
          
          // 删除模板配置文件
          await templateRenderer.removeTemplateConfig(targetDir);
          
          succeedSpinner('项目文件生成完成');
        } catch (error: any) {
          failSpinner('项目文件生成失败');
          logger.error(error.message);
        }

        // 更新模板使用时间
        await configManager.updateTemplateLastUsed(template.name);

        // 显示完成信息
        logger.newLine();
        logger.success(`项目创建成功！`);
        logger.newLine();

        if (templateConfig?.completeMessage) {
          const Handlebars = (await import('handlebars')).default;
          const template = Handlebars.compile(templateConfig.completeMessage);
          const message = template(answers);
          logger.log(message);
        } else {
          logger.log(chalk.gray('运行以下命令开始开发：'));
          logger.log(`  ${chalk.cyan(`cd ${projectName}`)}`);
          logger.log(`  ${chalk.cyan('npm install')} ${chalk.gray('# 安装依赖')}`);
          logger.log(`  ${chalk.cyan('npm run dev')} ${chalk.gray('# 启动项目')}`);
        }

        logger.newLine();
      } catch (error: any) {
        logger.error(`创建项目失败: ${error.message}`);
        process.exit(1);
      }
    });
}

